import React, { useEffect } from "react";
import { Box, Card, Divider, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { TEXT } from "../../dark-theme/palette";
import { trackEvent } from "../../services/GoogleAnalytics";
import { usePagesTranslation } from "../locales";

/**
 * Explains that native apps use the same Puzzle Interact web email and password,
 * and that web Premium credits apply in the apps.
 */
export default function HowToAppLogin() {
    const { t } = usePagesTranslation();

    useEffect(() => {
        trackEvent("entered", { name: "HowToAppLogin" });
    }, []);

    useEffect(() => {
        const title = t("howto.appLogin.title", "Sign in to the apps with your web account");
        document.title = `${title} | Puzzle Interact`;
        return () => {
            document.title = process.env.REACT_APP_SITE_NAME || "Puzzle Interact";
        };
    }, [t]);

    return (
        <Box sx={{ width: "100%", minHeight: "100vh", py: 4 }}>
            <Box sx={{ maxWidth: 800, mx: "auto", px: 3 }}>
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{ textAlign: "center", mb: 1.5, fontWeight: 700, color: "#3BA666" }}
                >
                    {t("howto.appLogin.title", "Sign in to the apps with your web account")}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        textAlign: "center",
                        mb: 4,
                        color: TEXT.secondary,
                        maxWidth: 640,
                        mx: "auto",
                    }}
                >
                    {t(
                        "howto.appLogin.intro",
                        "Become a web Premium member and use the same account in every Puzzle Interact app.",
                    )}
                </Typography>

                <Card sx={{ p: 3, mb: 3, boxShadow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                        {t("howto.appLogin.webTitle", "1. Sign in on the website")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {t(
                            "howto.appLogin.webBody",
                            "On puzzleinteract.com, tap Sign In and create or open your Puzzle Interact account with email and password. Upgrade to Premium when you want unlimited play and shared credits.",
                        )}
                    </Typography>
                    <Box
                        sx={{
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            p: 2.5,
                            bgcolor: "background.paper",
                        }}
                    >
                        <Typography variant="overline" sx={{ color: TEXT.secondary, letterSpacing: 1 }}>
                            {t("howto.appLogin.webMockLabel", "Website sign-in")}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                            Puzzle Interact
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gap: 1,
                                maxWidth: 320,
                            }}
                        >
                            <Box sx={mockFieldSx}>
                                {t("howto.appLogin.emailPlaceholder", "Email")}
                            </Box>
                            <Box sx={mockFieldSx}>
                                {t("howto.appLogin.passwordPlaceholder", "Password")}
                            </Box>
                            <Box
                                sx={{
                                    ...mockFieldSx,
                                    bgcolor: "#3BA666",
                                    color: "#fff",
                                    fontWeight: 700,
                                    textAlign: "center",
                                    borderColor: "#3BA666",
                                }}
                            >
                                {t("login.header-sign-in", "Sign In")}
                            </Box>
                        </Box>
                    </Box>
                </Card>

                <Card sx={{ p: 3, mb: 3, boxShadow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                        {t("howto.appLogin.appTitle", "2. Open the same login in the app")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {t(
                            "howto.appLogin.appBody",
                            "In Sliding Puzzle Quest, Jigsaw Muse, or Word Wheel Quest, open Settings and tap Sign in. The app shows the same web login page — enter the email and password you use on the website.",
                        )}
                    </Typography>
                    <Box
                        sx={{
                            width: 260,
                            mx: "auto",
                            borderRadius: "28px",
                            border: "10px solid #0b1220",
                            bgcolor: "#111a2e",
                            p: 2,
                            boxShadow: "0 12px 32px rgba(2,6,18,0.4)",
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ display: "block", textAlign: "center", color: TEXT.secondary, mb: 1 }}
                        >
                            {t("howto.appLogin.appMockLabel", "App sign-in")}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "#eef4ff" }}>
                            {t("howto.appLogin.appMockTitle", "Sign in with your web account")}
                        </Typography>
                        <Box sx={{ ...mockFieldSx, bgcolor: "rgba(255,255,255,0.08)", color: TEXT.secondary, mb: 1 }}>
                            {t("howto.appLogin.emailPlaceholder", "Email")}
                        </Box>
                        <Box sx={{ ...mockFieldSx, bgcolor: "rgba(255,255,255,0.08)", color: TEXT.secondary, mb: 1 }}>
                            {t("howto.appLogin.passwordPlaceholder", "Password")}
                        </Box>
                        <Box
                            sx={{
                                ...mockFieldSx,
                                bgcolor: "#3BA666",
                                color: "#fff",
                                fontWeight: 700,
                                textAlign: "center",
                                borderColor: "#3BA666",
                            }}
                        >
                            {t("login.header-sign-in", "Sign In")}
                        </Box>
                    </Box>
                </Card>

                <Card sx={{ p: 3, mb: 3, boxShadow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                        {t("howto.appLogin.creditsTitle", "3. Use your web credits in the apps")}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t(
                            "howto.appLogin.creditsBody",
                            "After you sign in, your web Premium plan, coins, and puzzle progress are available in the apps. You do not need a separate app account.",
                        )}
                    </Typography>
                </Card>

                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                    <Link component={RouterLink} to="/subscription/plans" sx={{ fontWeight: 700, color: "#3BA666" }}>
                        {t("howto.appLogin.premiumLink", "Become a web Premium member")}
                    </Link>
                    {" · "}
                    <Link component={RouterLink} to="/" sx={{ fontWeight: 600 }}>
                        {t("nav.home", "Home")}
                    </Link>
                </Typography>
            </Box>
        </Box>
    );
}

const mockFieldSx = {
    px: 1.5,
    py: 1.1,
    borderRadius: 1,
    border: "1px solid",
    borderColor: "divider",
    fontSize: "0.9rem",
    color: "text.secondary",
};
